import { useEffect, useContext, useCallback } from 'react';

import { useTypedParams } from 'react-router-typesafe-routes';

import { setSelectedCustomerId } from '../../../utils/appStorage';
import { getCustomerDetail } from '../../../api';
import { CustomerContext } from '../../../context-providers/CustomerContext';
import { Customer } from '../../../types';
import { ROUTES } from '../../../routes';

/**
 * Hook for CustomerDetailsPage
 * - Syncs URL customerId to localStorage (for CustomersListPage redirect)
 * - Fetches and sets selectedCustomer in context (for UnifiedDialog etc.)
 * - Fetches and sets customerDetail in context
 */
export const useCustomerPage = () => {
  const { customerId } = useTypedParams(ROUTES.customer);
  const { selectedCustomer, setSelectedCustomer, setCustomerDetail } = useContext(CustomerContext);

  const fetchAndSetCustomer = useCallback(async(id: number) => {
    try {
      const detail = await getCustomerDetail(id);
      const customer: Customer = {
        id: detail.id,
        full_name: detail.full_name,
        phone_number: detail.phone_number,
        email: detail.email,
        primary_email: detail.primary_email,
        primary_phone: detail.primary_phone,
        address: detail.address,
        created_at: detail.created_at,
      };
      setSelectedCustomer(customer);
      setCustomerDetail(detail);
    } catch (error) {
      console.error('Failed to fetch customer:', error);
      setSelectedCustomer(null);
      setCustomerDetail(null);
    }
  }, [setSelectedCustomer, setCustomerDetail]);

  useEffect(() => {
    if (customerId) {
      setSelectedCustomerId(customerId);

      // Fetch customer if not already selected or if ID changed
      if (!selectedCustomer || selectedCustomer.id !== customerId) {
        fetchAndSetCustomer(customerId);
      }
    }
  }, [customerId, selectedCustomer, fetchAndSetCustomer]);
};
